import { useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMemberSessionToken } from '../lib/sessionAuth';
import { flushActivityQueue, prepareActivityIdentity, syncResumeContinuity } from '../lib/activityIntelligence';
import { flushResumeSyncQueue, readResumeSyncQueue } from '../lib/resumeSync';

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_RETRY_TIMER_MS = 1_000;

const GlobalResumeSync = () => {
  const { isAuthenticated, user } = useAuth();
  const runningRef = useRef(false);
  const timerRef = useRef(null);

  const token = isAuthenticated && typeof window !== 'undefined'
    ? (getMemberSessionToken() || window.localStorage.getItem('admin_token')) : null;

  const scheduleNextRetry = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    const queue = readResumeSyncQueue();
    if (!queue.length) return;
    const nextAt = Math.min(...queue.map((item) => Date.parse(item.next_attempt_at || '') || Date.now()));
    const delay = Math.max(MIN_RETRY_TIMER_MS, nextAt - Date.now());
    timerRef.current = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('rastyle-resume-sync-pending'));
    }, delay);
  }, []);

  const syncPending = useCallback(async ({ hydrate = false } = {}) => {
    if (!API || runningRef.current) return;
    runningRef.current = true;
    try {
      if (isAuthenticated) prepareActivityIdentity(user?.user_id);
      await flushResumeSyncQueue({ apiBase: API, token });
      await flushActivityQueue({ apiBase: API, token });
      if (hydrate) await syncResumeContinuity({ apiBase: API, token, authenticated: isAuthenticated });
    } finally {
      runningRef.current = false;
      scheduleNextRetry();
    }
  }, [scheduleNextRetry, token, isAuthenticated, user?.user_id]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handlePending = () => syncPending();
    const handleOnline = () => syncPending({ hydrate: true });
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncPending();
    };

    syncPending({ hydrate: true });
    window.addEventListener('rastyle-resume-sync-pending', handlePending);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      window.removeEventListener('rastyle-resume-sync-pending', handlePending);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, syncPending]);

  return null;
};

export default GlobalResumeSync;
